import {
  Button,
  Col,
  Collapse,
  Empty,
  Form,
  Image,
  Input,
  Row,
  Select,
  Spin,
  Upload,
  message,
} from "antd";
import { PhoneInput } from "cmt-ui-kit";
import moment from "moment";
import "moment/locale/es";
import { Fragment, useCallback, useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import "react-phone-input-2/lib/style.css";
import {
  admissionService,
  getServicesSuggestions,
} from "../../../services/admission";
import { getCountries, getGenders, getAreas, getJobPositions, getProjects, getProject } from "../../../services/general";
import { getPrestationByAdmissionPatient } from "../../../services/orderDetails";
import { getPatients } from "../../../services/reservation";
import WebcamModal from "../../components/WebcamModal";
import usePatientPhoto from "../../components/hooks/usePatientPhoto";
import PatientPhotoButtons from "../../components/PatientPhotoButtons";
import { PaperClipOutlined } from "@ant-design/icons";
import useDNIPhoto from "../../components/hooks/useDNIPhoto";
import useFingerprintPhoto from "../../components/hooks/useFingerprintPhoto";
import FingerprintButtons from "../../components/FingerprintButtons";
import countrySettings from "../countrySettings.json";
import evaluationTypes from '../../../utils/evaluationType.json';
import { isProfessionRequiredByEvaluationType } from "../../../utils/evaluationRules";
import { useAdmissionCompanyData } from "../hooks/useAdmissionCompanyData";
import CompanyDataBlock from "./PatientFormPE/CompanyDataBlock";
import WorkerDataBlock from "./PatientFormPE/WorkerDataBlock";
import debounce from 'lodash/debounce';
import { getCookie } from '../../../services/cookies';

const { Panel } = Collapse;
const { format, validate, clean } = require("rut.js");

const calcAgeFromBirthdate = (birthdate, inputFormat = "YYYY-MM-DD") =>
  moment().diff(moment(birthdate, inputFormat), "years");

const normalizePhone = (phone) => {
  if (!phone || phone === "" || phone.length <= 4) return null;
  return phone.startsWith("+") ? phone : `+${phone}`;
};

const sanitizePatientPayload = (rawPatient) => {
  const patient = { ...rawPatient };

  if (!patient.id) patient.id = null;

  if (patient.documentType === "rut" && patient.documentNumber) {
    const [rutBody] = patient.documentNumber.split("-");
    patient.documentNumber = `${clean(rutBody)}-${patient.documentNumber.split("-")[1]}`;
  }

  if (patient.dateBirth) {
    patient.dateBirth = moment(patient.dateBirth, "YYYY-MM-DD").format("YYYY-MM-DD");
  }

  patient.phone = normalizePhone(patient.phone);

  delete patient.age;
  delete patient.reservation_date;

  return Object.fromEntries(Object.entries(patient).filter(([, v]) => v !== undefined));
};

const CaptureDNIButtons = ({ title, dniPhoto, field }) => (
  <Col className="buttonsBackground mb-20 pl-30 pt-30 pb-30" align="middle">
    <Row align="middle" gutter={15}>
      <Col span={4}>
        <div className="document-title">{title}</div>
      </Col>
      <Col>
        <Button
          type="primary"
          className="cmt-button secondary"
          onClick={dniPhoto.openModal}
          size="large"
        >
          <span className="icon-camera-bold mr-10" />
          Capturar DNI
        </Button>
      </Col>
      <Col>
        {dniPhoto.dniFile ? (
          <div>{dniPhoto.dniFile.name}</div>
        ) : (
          <Upload
            showUploadList={false}
            beforeUpload={(file) => {
              dniPhoto.beforeUpload(file);
              return false;
            }}
          >
            <Button type="primary" className="cmt-button secondary outline" size="large">
              <PaperClipOutlined style={{ color: "#223a70", fontSize: 20 }} />
              Cargar DNI
            </Button>
          </Upload>
        )}
      </Col>
      <Col>
        <Button
          type="primary"
          className={`deleteButton cmt-button ${dniPhoto.imageSrc && !dniPhoto.imageSrc?.includes("missing")
            ? "secondary non-outline"
            : "disabled non-outline"
            }`}
          onClick={dniPhoto.resetImage}
          disabled={dniPhoto.imageSrc?.includes("missing") || !dniPhoto.imageSrc}
          size="large"
        >
          <span className="icon-system-delete" />
        </Button>
      </Col>
    </Row>
    <Row>
      {dniPhoto.imageSrc && !dniPhoto.imageSrc?.includes("missing") && (
        <Col lg={24} xs={24} className="mt-30">
          <Form.Item name={field}>
            <Row>
              <Col lg={12} xs={24} span={6} align="middle">
                {dniPhoto.imageType === "application/pdf" ? (
                  <embed
                    src={dniPhoto.imageSrc}
                    alt="patient"
                    style={{ maxWidth: 276, height: 166 }}
                    className="mb-20"
                  />
                ) : (
                  <Image
                    src={dniPhoto.imageSrc}
                    alt="patient"
                    style={{ maxWidth: 276, height: 166 }}
                    className="mb-20"
                  />
                )}
                <Input type="hidden" />
              </Col>
            </Row>
          </Form.Item>
        </Col>
      )}
    </Row>
  </Col>
);

const PatientFormPE = ({
  form,
  reservation,
  patientToAdmission,
  setPatientToAdmission,
  setCurrentStep,
  providerCode,
}) => {
  const countryCode = getCookie('country_code');
  const [loading, setLoading] = useState({
    patient: false,
    servicesByPatient: false,
    nextStep: false,
    gender: false,
    countries: false,
    jobData: false,
    suggestions: false,
  });
  const [genders, setGenders] = useState();
  const [countries, setCountries] = useState();
  const [documentType, setDocumentType] = useState("dni");
  const [disabled, setDisabled] = useState({ blockingPatient: false });
  const [isJobPositionRequired, setIsJobPositionRequired] = useState(true);
  const [showMessage, setShowMessage] = useState({ datePicker: false });
  const [areasList, setAreasList] = useState([]);
  const [jobPositionsList, setJobPositionsList] = useState([]);
  const [projectsList, setProjectsList] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [activePanels, setActivePanels] = useState([]);

  const { t } = useTranslation();

  const patientPhoto = usePatientPhoto({ form, patientToAdmission, setLoading, countryCode });
  const frontDNIPhoto = useDNIPhoto({ form, patientToAdmission, setLoading, countryCode, field: "card_front" });
  const backDNIPhoto = useDNIPhoto({ form, patientToAdmission, setLoading, countryCode, field: "card_back" });
  const fingerprintPhoto = useFingerprintPhoto({ form, patientToAdmission, setPatientToAdmission, setLoading, countryCode, field: 'fingerprint' });

  const companyData = useAdmissionCompanyData({ form, reservation });

  const fetchGenders = useCallback(async () => {
    setLoading(prev => ({ ...prev, gender: true }));
    try {
      const response = await getGenders();
      setGenders(response || []);
    } catch (error) {
      console.error(error);
      message.error({ content: "Error en fetch de géneros", style: { marginTop: "45vh" } });
    } finally {
      setLoading(prev => ({ ...prev, gender: false }));
    }
  }, []);

  const fetchCountries = useCallback(async () => {
    setLoading(prev => ({ ...prev, countries: true }));
    try {
      const response = await getCountries();
      setCountries(response || []);
    } catch (error) {
      console.error(error);
      message.error({ content: "Error en fetch de países", style: { marginTop: "45vh" } });
    } finally {
      setLoading(prev => ({ ...prev, countries: false }));
    }
  }, []);

  const fetchJobData = useCallback(async (branchOfficeId, companyId) => {
    if (!companyId) return;
    setLoading(prev => ({ ...prev, jobData: true }));
    try {
      const [areas, positions] = await Promise.all([
        getAreas({ branch_office_id: branchOfficeId, company_id: companyId }),
        getJobPositions({ branch_office_id: branchOfficeId, company_id: companyId }),
      ]);
      setAreasList(areas);
      setJobPositionsList(positions);
    } catch (error) {
      console.error(error);
      message.error({ content: "Error al cargar áreas y puestos", style: { marginTop: "45vh" } });
    } finally {
      setLoading(prev => ({ ...prev, jobData: false }));
    }
  }, []);

  const handleAreaCreated = useCallback((newArea) => {
    setAreasList(prev => [...prev, newArea]);
  }, []);

  const handleJobPositionCreated = useCallback((newJobPosition) => {
    setJobPositionsList(prev => [...prev, newJobPosition]);
  }, []);

  const fetchProjects = useCallback(debounce(async (query) => {
    if (!query) return;
    setLoadingProjects(true);
    try {
      const response = await getProjects({ query, company_id: reservation?.company_id });
      setProjectsList(response);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingProjects(false);
    }
  }, 500), []);

  const fetchSuggestions = async (patientId) => {
    setLoading(prev => ({ ...prev, suggestions: true }));
    try {
      return await getServicesSuggestions({ reservationId: reservation.id, patientId, providerCode });
    } catch (error) {
      message.error({ content: `Error al traer las sugerencias`, style: { marginTop: "45vh" } });
      console.error(error);
    } finally {
      setLoading(prev => ({ ...prev, suggestions: false }));
    }
  };

  const fetchPatient = async (docType, documentNumber) => {
    setLoading(prev => ({ ...prev, patient: true }));
    let response;
    let isBlocked = false;
    try {
      response = await admissionService.getPatient({ document_type: docType, document_number: documentNumber });
    } catch (error) {
      console.error(error);
      let errorMessage = 'Error al buscar al paciente';
      if (error === 'blocked') {
        errorMessage = `No es posible admisionar al paciente ${documentNumber}.`;
        resetPatientFields(docType, documentNumber);
        isBlocked = true;
      }
      message.error({ content: errorMessage, style: { marginTop: '30vh', fontSize: 16 } });
    } finally {
      setLoading(prev => ({ ...prev, patient: false }));
      setDisabled({ blockingPatient: isBlocked });
      return response;
    }
  };

  const handleEvaluationTypeChange = useCallback((evaluationType) => {
    const isRequired = isProfessionRequiredByEvaluationType(evaluationType, countryCode, evaluationTypes);
    setIsJobPositionRequired(isRequired);

    if (!isRequired) {
      form.setFieldsValue({ job_position_id: undefined, profession: null });
    }
  }, [countryCode, form]);

  const resetPatientFields = (docType, documentNumber) => {
    form.setFieldsValue({
      id: undefined,
      documentNumber: docType === "rut" ? format(documentNumber) : documentNumber,
      userFirstName: undefined,
      userLastName: undefined,
      genderId: undefined,
      dateBirth: undefined,
      age: undefined,
      phone: undefined,
      email: undefined,
      evaluation_type: undefined,
      project_id: undefined,
      area_id: undefined,
      job_position_id: undefined,
      profession: null,
      external_id: undefined,
      suggestions: [],
    });
    setIsJobPositionRequired(true);
  };

  const handleOnBlur = async (event) => {
    const inputValue = event.target.value;
    if (!inputValue) return;

    setLoading(prev => ({ ...prev, patient: true }));
    try {
      let result;
      if (documentType === "rut") {
        if (validate(inputValue)) {
          result = await fetchPatient("rut", format(inputValue, { dots: false }));
        }
      } else {
        result = await fetchPatient(documentType, inputValue);
      }

      if (result?.length && result[0]) {
        await handlePatientFound(result[0]);
      } else {
        handlePatientNotFound(inputValue);
      }
    } catch (error) {
      console.error(error);
      message.error({ content: "Error al buscar información del trabajador", style: { marginTop: "45vh" } });
    } finally {
      setLoading(prev => ({ ...prev, patient: false }));
    }
  };

  const handlePatientFound = async (searchPatient) => {
    patientPhoto.fetchPatientIdCard({ id: searchPatient.id });
    frontDNIPhoto.fetchPhoto({ id: searchPatient.id });
    backDNIPhoto.fetchPhoto({ id: searchPatient.id });
    fingerprintPhoto.fetchFingerprintPhoto({ id: searchPatient.id });

    setLoading(prev => ({ ...prev, servicesByPatient: true }));
    const [prestationResponse, suggestions, reservationPatients] = await Promise.all([
      getPrestationByAdmissionPatient({
        reservation_id: reservation.id,
        patient_id: searchPatient.id,
        provider_code: providerCode,
      }),
      fetchSuggestions(searchPatient.id),
      getPatients({ reservation_id: reservation.id }).catch(() => []),
    ]);
    setLoading(prev => ({ ...prev, servicesByPatient: false }));

    const previousExams = prestationResponse ?? [];
    const admissionPatientId = previousExams[0]?.admission_patient_id;
    let evaluationType = null;
    let cleanedSuggestions = [];

    if (suggestions?.length || prestationResponse?.length) {
      evaluationType = prestationResponse?.length ? prestationResponse[0].evaluation_type : suggestions[0].evaluation_type;
      handleEvaluationTypeChange(evaluationType);
      cleanedSuggestions = prestationResponse?.length ?
        prestationResponse.map((admitted) => {
          const cleanedSuggestion = { ...admitted };
          cleanedSuggestion.selectedKeywords = cleanedSuggestion.selected_keywords;

          delete cleanedSuggestion.selected_keywords;
          delete cleanedSuggestion.evaluation_type;

          cleanedSuggestion.additionals = cleanedSuggestion.admitted_exams
            .filter((exam) => !cleanedSuggestion.battery_exams.some((includeExam) => includeExam.id === exam.id))
            .map((exam) => ({ ...exam, isAdditional: true }));

          if (cleanedSuggestion.type === 'Battery' && cleanedSuggestion.battery_exams?.length > 0) {
            cleanedSuggestion.department_id = cleanedSuggestion.battery_exams[0].department_id;
          }

          return cleanedSuggestion;
        }) :
        suggestions.map((suggestion) => {
          const cleanedSuggestion = { ...suggestion };
          delete cleanedSuggestion.evaluation_type;
          delete cleanedSuggestion.profession;

          if (cleanedSuggestion.type === 'Battery' && cleanedSuggestion.battery_exams?.length > 0) {
            cleanedSuggestion.department_id = cleanedSuggestion.battery_exams[0].department_id ?? cleanedSuggestion.department_id;
          }

          return cleanedSuggestion;
        })
    }

    const preadmissionPatient = reservationPatients?.find(patient => patient.id === searchPatient.id);
    const admittedPatient = prestationResponse?.[0] ?? null;

    if (preadmissionPatient?.project_id || admittedPatient?.project_id) {
      getProject({ id: admittedPatient?.project_id || preadmissionPatient.project_id })
        .then(([project]) => {
          if (project) setProjectsList([project]);
        })
        .catch(err => {
          console.error('Error fetching project:', err);
          message.error({ content: "Error al cargar la información del proyecto", style: { marginTop: "45vh" } });
        });
    }

    const birthdate = searchPatient.birthdate
      ? moment(searchPatient.birthdate, "YYYY-MM-DD")
      : null;

    form.setFieldsValue({
      id: searchPatient.id,
      documentType: searchPatient.document_type,
      documentNumber: documentType === "rut"
        ? format(searchPatient.document_number)
        : searchPatient.document_number,
      ...(documentType === "passport" && { countryCode: searchPatient.country }),
      userFirstName: searchPatient.names,
      userLastName: searchPatient.last_name,
      email: searchPatient.email,
      genderId: searchPatient.gender !== null ? parseInt(searchPatient.gender) : null,
      dateBirth: birthdate,
      age: birthdate ? calcAgeFromBirthdate(searchPatient.birthdate) : undefined,
      phone: PhoneInput.formatPhone(searchPatient.phone, countryCode),
      evaluation_type: evaluationType,
      ...((preadmissionPatient || admittedPatient) && {
        project_id: admittedPatient?.project_id ?? preadmissionPatient?.project_id,
        area_id: admittedPatient?.area_id ?? preadmissionPatient?.area_id,
        job_position_id: admittedPatient?.job_position_id ?? preadmissionPatient?.job_position_id,
        external_id: preadmissionPatient?.external_id || undefined,
        profession: (admittedPatient?.job_position_id ?? preadmissionPatient?.job_position_id)
          ? (jobPositionsList?.find(p => p.id === (admittedPatient?.job_position_id ?? preadmissionPatient?.job_position_id))?.name || null)
          : null,
      }),
    });

    setPatientToAdmission(prev => ({
      ...prev,
      preadmissionPrestations: cleanedSuggestions,
      previousPrestations: previousExams,
      admissionPatientId,
      prestations: cleanedSuggestions,
      evaluation_type: evaluationType,
      originalProfession: searchPatient.position,
      hasPrefilledEvaluationType: evaluationType != null && countrySettings.patientForm.hasPrefilledEvaluationType[countryCode],
      consents: null,
      laboratoryPrestations: [],
      diabetic: null,
    }));
    setLoading(prev => ({ ...prev, servicesByPatient: false }));
  };

  const handlePatientNotFound = (inputValue) => {
    resetPatientFields(documentType, inputValue);
    patientPhoto.resetImage();
    frontDNIPhoto.resetImage();
    backDNIPhoto.resetImage();
    fingerprintPhoto.resetImage();

    setPatientToAdmission(prev => ({
      ...prev,
      preadmissionPrestations: [],
      previousPrestations: [],
      admissionPatientId: null,
      prestations: [],
      evaluation_type: null,
      consents: null,
      laboratoryPrestations: [],
      diabetic: null,
      originalProfession: null,
    }));
  };

  const handleNextStep = async () => {
    setLoading(prev => ({ ...prev, nextStep: true }));
    try {
      const formValues = await form.validateFields();
      const definedFormValues = Object.fromEntries(
        Object.entries(formValues).filter(([, v]) => v !== undefined)
      );

      const patientPayload = sanitizePatientPayload({ ...patientToAdmission, ...definedFormValues, countryCode });
      setPatientToAdmission(patientPayload);
      setCurrentStep(1);
    } catch (errorInfo) {
      if (errorInfo?.errorFields?.length) {
        setActivePanels(['company', 'worker', 'identity']);
        return;
      }
      console.error(errorInfo);
      message.error({ content: "Error al validar o procesar la información para continuar", style: { marginTop: "45vh" } });
    } finally {
      setLoading(prev => ({ ...prev, nextStep: false }));
    }
  };

  const disabledDate = (current) => {
    const overAge = moment().subtract(8, "years");
    return current && overAge < current;
  };

  const handleBirthDateChange = (_, dateStr) => {
    const age = moment().diff(moment(dateStr, "DD-MM-YYYY"), "years");
    setShowMessage({ datePicker: age < 18 });
    form.setFieldsValue({ age: age >= 0 ? age : undefined });
  };

  const isInitializedRef = useRef(false);

  useEffect(() => {
    if (isInitializedRef.current) return;
    isInitializedRef.current = true;

    try {
      if (patientToAdmission) {
        const initialEvaluationType = patientToAdmission.evaluation_type;
        if (initialEvaluationType) {
          handleEvaluationTypeChange(initialEvaluationType);
        }

        const initialAge = patientToAdmission.dateBirth
          ? calcAgeFromBirthdate(patientToAdmission.dateBirth)
          : undefined;

        form.setFieldsValue({
          id: patientToAdmission["id"],
          documentType: patientToAdmission["documentType"],
          documentNumber: patientToAdmission["documentType"] === "rut"
            ? format(patientToAdmission["documentNumber"])
            : patientToAdmission["documentNumber"],
          countryCode: patientToAdmission["documentType"] === "passport"
            ? patientToAdmission["countryCode"]
            : undefined,
          genderId: patientToAdmission["genderId"],
          email: patientToAdmission["email"],
          userFirstName: patientToAdmission["userFirstName"],
          userLastName: patientToAdmission["userLastName"],
          dateBirth: patientToAdmission.dateBirth
            ? moment(patientToAdmission["dateBirth"], "YYYY-MM-DD")
            : moment(),
          age: initialAge,
          phone: PhoneInput.formatPhone(patientToAdmission["phone"], countryCode),
          pictureIdCard: patientToAdmission.pictureIdCard,
          evaluation_type: initialEvaluationType,
          project_id: patientToAdmission["project_id"],
          area_id: patientToAdmission["area_id"],
          job_position_id: patientToAdmission["job_position_id"],
          profession: patientToAdmission["profession"] || null,
          external_id: patientToAdmission["external_id"],
        });
        setDocumentType(patientToAdmission["documentType"]);
      } else {
        form.setFieldsValue({ documentType });
      }

      fetchGenders();
    } catch (error) {
      console.error(error);
      message.error({ content: "Error al inicializar los datos del paciente en el formulario", style: { marginTop: "45vh" } });
    }
  }, [patientToAdmission, form, documentType, countryCode, fetchGenders, handleEvaluationTypeChange]);

  useEffect(() => {
    const confirmRefreshPage = (event) => {
      event.preventDefault();
      event.returnValue = "¿Estás seguro que deseas abandonar?";
    };
    window.addEventListener("beforeunload", confirmRefreshPage);
    return () => window.removeEventListener("beforeunload", confirmRefreshPage);
  }, []);

  useEffect(() => {
    if (documentType === "passport") {
      fetchCountries();
    }
  }, [documentType, fetchCountries]);

  useEffect(() => {
    if (reservation?.branch_office_id && reservation?.company_id) {
      fetchJobData(reservation.branch_office_id, reservation.company_id);
    }
  }, [reservation?.branch_office_id, reservation?.company_id, fetchJobData]);

  const handleJobPositionChange = useCallback((jobPositionId) => {
    const selectedPosition = jobPositionsList?.find(p => p.id === jobPositionId);
    form.setFieldsValue({ profession: selectedPosition?.name || null });
  }, [jobPositionsList, form]);

  return (
    <Spin className="cmt-spin" spinning={loading.patient || loading.servicesByPatient}>
      <Row justify="center" className="admission-settings__patient-form mt-50">
        <Fragment>
          <Col lg={15} xs={22}>
            <Form
              form={form}
              size="large"
              layout="vertical"
              className="cmt-form"
              initialValues={reservation ? {
                user_id: reservation.user_id,
                company_id: reservation.company_id,
                outsourcing_id: reservation.outsourcing_id || undefined,
                third_company_id: reservation.third_company_id || undefined,
                invoice_company_id: reservation.invoice_company_id || undefined,
                cost_center_id: reservation.cost_center?.id || undefined,
                child_company_id: reservation.child_company_id || undefined,
                branch_office_id: reservation.branch_office_id,
              } : {}
              }
            >
              <Form.Item name="id" hidden><Input type="hidden" /></Form.Item>
              <Form.Item name="profession" hidden><Input type="hidden" /></Form.Item>
              <Form.Item name="prestations" hidden><Select type="hidden" /></Form.Item>
              <Form.Item name="suggestions" hidden><Select type="hidden" /></Form.Item>

              <Collapse expandIconPosition="end" activeKey={activePanels} onChange={setActivePanels}>

                <Panel forceRender={true} header="Datos generales de la empresa" key="company">
                  <CompanyDataBlock companyData={companyData} />
                </Panel>

                <Panel forceRender={true} header="Datos trabajador" key="worker">
                  <Spin className="cmt-spin" spinning={loading.jobData}>
                    <WorkerDataBlock
                      form={form}
                      reservation={reservation}
                      workerData={{
                        documentType,
                        setDocumentType,
                        handleOnBlur,
                        genders,
                        countries,
                        areasList,
                        jobPositionsList,
                        projectsList,
                        loadingProjects,
                        isJobPositionRequired,
                        showMessage,
                        handleEvaluationTypeChange,
                        handleBirthDateChange,
                        disabledDate,
                        fetchProjects,
                        loadingGender: loading.gender,
                        loadingCountries: loading.countries,
                        handleJobPositionChange,
                        handleAreaCreated,
                        handleJobPositionCreated,
                      }}
                    />
                  </Spin>
                </Panel>

                <Panel forceRender={true} header="Validación de identidad" key="identity">
                  <Row>
                    <Col lg={14} xs={24} className="mt-20">
                      <h2 className="mb-20">{t("admissionPage.patientForm.patientPhotoTitle")}</h2>
                      <PatientPhotoButtons patientPhoto={patientPhoto}>
                        <Form.Item name="pictureIdCard">
                          <Row>
                            <Col lg={12} xs={24}>
                              {patientPhoto.imageSrc && !patientPhoto.imageSrc.includes("missing") ? (
                                <Image
                                  src={patientPhoto.imageSrc}
                                  alt="patient"
                                  style={{ width: 276, height: 284 }}
                                  className="mb-20"
                                />
                              ) : (
                                <Empty
                                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                                  description="Sin foto"
                                  className="mb-20"
                                />
                              )}
                              <Input type="hidden" />
                            </Col>
                          </Row>
                        </Form.Item>
                      </PatientPhotoButtons>
                    </Col>

                    {countrySettings.patientForm.identityValidation[countryCode] && (
                      <Col lg={24} xs={24} className="mt-40 identity-validation">
                        <h2 className="mb-20">{t("admissionPage.patientForm.identityValidationTitle")}</h2>
                        <p className="instruction-text">{t("admissionPage.patientForm.identityInstruction")}</p>
                        <p>{t("admissionPage.patientForm.identityFileConstraints")}</p>
                        <CaptureDNIButtons
                          title={t("admissionPage.patientForm.cardButtonFront")}
                          dniPhoto={frontDNIPhoto}
                          field="card_front"
                        />
                        <CaptureDNIButtons
                          title={t("admissionPage.patientForm.cardButtonReverse")}
                          dniPhoto={backDNIPhoto}
                          field="card_back"
                        />
                      </Col>
                    )}

                    {countrySettings.patientForm.patientFingerprint[countryCode] && (
                      <Col lg={14} xs={24} className="mt-40 identity-validation">
                        <h2 className="mb-20">{t('admissionPage.patientForm.patientFingerprint')}</h2>
                        <p className="instruction-text">{t("admissionPage.patientForm.patientFingerprintInstruction")}</p>
                        <FingerprintButtons fingerprintPhoto={fingerprintPhoto} field="fingerprint" />
                      </Col>
                    )}
                  </Row>
                </Panel>

              </Collapse>
            </Form>
          </Col>

          <Col lg={15} xs={22} className="mt-50 mb-60">
            <Row justify="end">
              <Col xl={3} lg={4} xs={24}>
                <Button
                  loading={loading.nextStep || loading.servicesByPatient}
                  onClick={handleNextStep}
                  className={`cmt-button primary ${disabled?.blockingPatient ? 'disabled' : ''}`}
                  type="primary"
                  size="large"
                  disabled={disabled?.blockingPatient}
                >
                  Siguiente
                </Button>
              </Col>
            </Row>
          </Col>
        </Fragment>
      </Row>

      <WebcamModal {...patientPhoto} countrySettings={countrySettings} countryCode={countryCode} />
      <WebcamModal {...frontDNIPhoto} countrySettings={countrySettings} countryCode={countryCode} />
      <WebcamModal {...backDNIPhoto} countrySettings={countrySettings} countryCode={countryCode} />
    </Spin>
  );
};

export default PatientFormPE;
